/**
 * Pull-to-refresh gesture maths, kept free of React and the DOM so the rules
 * can be unit-tested directly.
 *
 * A standalone iOS PWA gets no native pull-to-refresh (Safari only provides it
 * in a browser tab), so the app has to implement the gesture itself.
 */

/** How far (px) the finger must travel down before a refresh is committed. */
export const PULL_THRESHOLD = 70

/**
 * Pulling past the threshold keeps moving the indicator, but at a fraction of
 * the finger's speed, which reads as resistance and signals a limit.
 */
export const PULL_RESISTANCE = 0.4

/** Hard ceiling on the indicator's travel, however far the finger goes. */
export const MAX_PULL = 110

/**
 * Maps raw finger travel onto the distance the indicator actually moves.
 * Below the threshold it tracks the finger 1:1; beyond it the surplus is
 * damped, and the total is capped.
 */
export const resolvePullDistance = (rawDistance: number): number => {
  if (rawDistance <= 0) return 0
  if (rawDistance <= PULL_THRESHOLD) return rawDistance

  const surplus = (rawDistance - PULL_THRESHOLD) * PULL_RESISTANCE
  return Math.min(PULL_THRESHOLD + surplus, MAX_PULL)
}

/** Whether releasing at this travel distance should trigger a refresh. */
export const shouldTriggerRefresh = (rawDistance: number): boolean => rawDistance >= PULL_THRESHOLD

/**
 * The gesture is only allowed to start at the very top of the scroller —
 * otherwise a normal upward scroll through the page would fight it.
 */
export const canStartPull = (scrollTop: number): boolean => scrollTop <= 0
